import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useDraggable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS, Coordinates } from '@dnd-kit/utilities';
import { Box, Stack, Typography, alpha } from '@mui/material';
import { ReactNode, cloneElement, useEffect, useRef, useState } from 'react';
import { useLocales } from 'src/locales';

const Handler = ({
  children,
  coordinates,
  enabled,
}: {
  children: ReactNode;
  coordinates: Coordinates;
  enabled?: boolean;
}) => {
  const { attributes, listeners, transform, setNodeRef } = useDraggable({
    id: 'draggable',
    disabled: !enabled,
  });
  const { translate } = useLocales();
  return (
    <Box
      sx={{ zIndex: -1, height: 1, cursor: enabled ? 'grab' : 'default' }}
      {...attributes}
      {...listeners}
    >
      {cloneElement(children as any, {
        ref: setNodeRef,
        style: {
          transform: CSS.Transform.toString({
            scaleX: 1,
            scaleY: 1,
            x: coordinates.x,
            y: (transform?.y || 0) + coordinates.y,
          }),
        },
      })}
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        sx={{
          position: 'absolute',
          inset: 0,
          height: '100%',
          width: '100%',
          visibility: enabled ? 'visible' : 'hidden',
        }}
      >
        <Box
          border={1}
          sx={(theme) => ({
            borderColor: theme.palette.action.active,
            color: theme.palette.common.white,
            borderRadius: 1,
            borderStyle: 'dashed',
            backgroundColor: alpha(theme.palette.action.active, 0.48),
            p: 1,
          })}
        >
          <Typography variant="body2">
            {translate('collaborate.docs.cover.drag_to_reposition')}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

type Props = {
  children: React.ReactNode;
  topOffset: number;
  setTopOffset: (value: number) => void;
  enabled?: boolean;
};

export const CoverReposition = ({
  children,
  topOffset = 0,
  enabled,
  setTopOffset,
}: Props) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const [coordinates, setCoordinates] = useState<Coordinates>({
    x: 0,
    y: topOffset,
  });
  const mouseSensor = useSensor(MouseSensor, {});
  const touchSensor = useSensor(TouchSensor, {});
  const keyboardSensor = useSensor(KeyboardSensor, {});
  const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor);

  useEffect(() => {
    setTopOffset(coordinates.y);
  }, [coordinates.y, setTopOffset]);

  useEffect(() => {
    if (!enabled) {
      setCoordinates({ x: 0, y: topOffset });
    }
  }, [enabled, topOffset]);

  return (
    <DndContext
      sensors={sensors}
      onDragEnd={({ delta }) => {
        setCoordinates({
          x: delta.x,
          y: coordinates.y + delta.y,
        });
      }}
      modifiers={[
        restrictToVerticalAxis,
        ({ transform, draggingNodeRect: rect }) => {
          if (!rect) return transform;
          const boundingRect = parentRef.current?.getBoundingClientRect();
          if (!boundingRect) return transform;

          const currentOffset = transform.y + +coordinates.y;
          const height =
            boundingRect.height - rect.height - +coordinates.y - transform.y;

          if (currentOffset >= 0)
            return { ...transform, y: transform.y - currentOffset };

          if (height >= 0) return { ...transform, y: transform.y + height };

          return transform;
        },
      ]}
    >
      <div ref={parentRef} style={{ width: '100%', height: '100%' }}>
        <Handler coordinates={coordinates} enabled={enabled}>
          {children}
        </Handler>
      </div>
    </DndContext>
  );
};
